'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import * as React from 'react';

import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname } from 'next/navigation';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);

const DialogClose = DialogPrimitive.Close;

DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'ea-dialog__content-overlay',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean;
    overlayClassName?: string;
  }
>(({ className, children, hideClose, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={cn(overlayClassName)} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-[92vw] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-hidden rounded-lg border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:max-w-[450px] md:w-full',
        'ea-dialog__content',
        className,
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <Cross2Icon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      'ea-dialog__footer',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      'ea-dialog__title',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', 'ea-dialog__description', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

type DialogConfig = {
  id: string;
  content: React.ReactNode;
  onClose?: () => void;
};

type DialogStoreState = {
  dialogs: Record<string, DialogConfig>;
};

type DialogStoreActions = {
  open: (config: DialogConfig) => void;
  close: (id: string) => void;
  closeAll: () => void;
};

const [DialogContextProvider, useDialogStore] = createContextStore<
  DialogStoreState,
  DialogStoreActions
>(
  {
    dialogs: {},
  },
  set => ({
    open: (config: DialogConfig) =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          [config.id]: config,
        },
      })),
    close: (id: string) =>
      set(state => {
        const dialogs = { ...state.dialogs };
        delete dialogs[id];
        return { dialogs };
      }),
    closeAll: () =>
      set(() => ({
        dialogs: {},
      })),
  }),
);

/**
 * It is important to always put dialog provider under context provider you want to use in dialog content.
 */
function DialogProvider(props: React.PropsWithChildren) {
  return (
    <DialogContextProvider>
      <DialogProviderRenderer />
      {props.children}
    </DialogContextProvider>
  );
}

function DialogProviderRenderer() {
  const dialogs = useDialogStore(s => s.dialogs);
  const close = useDialogStore(s => s.close);
  const closeAll = useDialogStore(s => s.closeAll);

  const pathname = usePathname();

  React.useEffect(() => {
    closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      {Object.values(dialogs).map(dialog => (
        <Dialog
          key={dialog.id}
          open
          onOpenChange={open => {
            if (!open) {
              dialog.onClose?.();
              close(dialog.id);
            }
          }}
        >
          {dialog.content}
        </Dialog>
      ))}
    </>
  );
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogProvider,
  DialogTitle,
  DialogClose,
  DialogTrigger,
  useDialogStore,
};
export type { DialogConfig };
